import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: string[];
}

/**
 * Componente que protege rutas, verificando autenticación y opcionalmente roles permitidos.
 * Redirige al login si el usuario no está autenticado.
 * Redirige al dashboard si el usuario no tiene el rol requerido.
 */
export default function ProtectedRoute({ children, allowedRoles }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  // Mostrar spinner mientras se verifica la autenticación
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Verificando sesión...</p>
        </div>
      </div>
    );
  }

  // Redirigir al login si no está autenticado
  if (!isAuthenticated) {
    // Guardar la ruta intentada para redirigir después del login
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Verificar roles si se especifican
  if (allowedRoles && allowedRoles.length > 0 && user) {
    if (!allowedRoles.includes(user.rol)) {
      // Usuario no tiene el rol requerido, redirigir al dashboard
      return <Navigate to="/login" replace />;
    }
  }

  // Usuario autenticado y con rol permitido, mostrar el contenido
  return <>{children}</>;
}
